import { observer } from "mobx-react-lite"
import React, { FC, useCallback } from "react"
import { Appearance, SafeAreaView, TouchableOpacity, ViewStyle, Dimensions, Platform } from "react-native"
import { Button, JalaIcon, Text } from "../../../../components"
import { useStores } from "../../../../models"
import moment from "moment"
import { Icon, useDisclose, View } from "native-base"
import { translate } from "../../../../i18n"
import { navigate } from "../../../../navigators"
import { color } from "../../../../theme"
import Modal from 'react-native-modal';
import JalaAnalytic from "../../../../utils/jala-analytic"

const windowWidth = Dimensions.get("window").width

const TOP_BAR_CONTAINER: ViewStyle = {
  justifyContent: "center",
}

const TOP_BAR_RIGHT_BUTTON: ViewStyle = {
  paddingHorizontal: 16,
  paddingLeft: 0
}

const TOP_BAR_MORE_BUTTON: ViewStyle = {
  paddingLeft: 0,
  paddingRight: 16,
  ...Platform.select({ web: { paddingRight: 16 } })
}

const MODAL: ViewStyle = {
  margin: 0,
  justifyContent: 'flex-start',
  alignItems: 'flex-end',
}

const MODAL_CONTENT: ViewStyle = {
  width: windowWidth * 0.7,
  paddingTop: 20,
  paddingHorizontal: 10,
  backgroundColor: color.palette.white,
  borderRadius: 10,
}

const BTN_MENU: ViewStyle = {
  flexDirection: 'row',
  alignItems: 'center',
  marginBottom: 20
}

export const CycleDetailTitle: FC<{ colorScheme?: "light" | "dark", tintColor?: string }> = observer(function CycleDetailTitle(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), tintColor } = props
  const { appStore: { farmStore: { pondStore: { pond, cycleStore} } } } = useStores();
  const dateFormat = 'DD MMM YYYY';

  return <View maxWidth={`${windowWidth - 184}px`}>
    <Text text={pond.name || '-'} preset="title2" colorScheme={colorScheme} color={tintColor} numberOfLines={1} isTruncated />
    <Text text={cycleStore?.cycle?.started_at && `${moment(cycleStore?.cycle?.started_at).format(dateFormat)} - ${cycleStore?.cycle?.finished_at ? moment(cycleStore?.cycle?.finished_at).format(dateFormat) : translate('now')}` || '-'} preset="body3" colorScheme={colorScheme} color={tintColor} />
  </View>
})

export const CycleDetailRefresh: FC = observer(function CycleDetailRefresh(props) {
  const { appStore, appStore: { farmStore: { pondStore: { cycleStore } }, isOffline } } = useStores();

  const onPress = useCallback(() => {
    appStore.checkNetworkConnection().then(() => {
      if (!isOffline) cycleStore.initAll()
    }).catch()
  }, [appStore, cycleStore, isOffline])

  return (
    <View style={TOP_BAR_CONTAINER}>
      <TouchableOpacity style={TOP_BAR_RIGHT_BUTTON} onPress={onPress}>
        <Icon as={JalaIcon} name="refresh" size="24px" color={color.palette.white} />
      </TouchableOpacity>
    </View>
  )
})

export const CycleDetailGraph: FC = observer(function CycleDetailGraph(props) {
  const onPress = useCallback(() => {
    JalaAnalytic.log('Mobile app: pond - open graph');
    navigate('Home.Stack', 'Home.Cycle.Graph');
  }, []);

  return (
    <View style={TOP_BAR_CONTAINER}>
      <TouchableOpacity style={TOP_BAR_RIGHT_BUTTON} onPress={onPress}>
        <JalaIcon name="graph-insight" size={24} color={color.palette.white}/>
      </TouchableOpacity>
    </View>
  )
})

const ButtonMenu = ({ colorScheme = "light" as "light"|"dark", label, icon, onPress }) => (
  <Button testID={`jala:id/${label}-button`}  preset="link" colorScheme={colorScheme} onPress={onPress} style={BTN_MENU}>
    <JalaIcon name={icon} size={28} color={color.primary} />
    <Text tx={label} preset="title3" color={color.palette.darkGrey} colorScheme={colorScheme} ml="10px" flex={1} />
  </Button>
)

const MenuOptions: FC<{
  isOpen?: boolean,
  onClose?: () => void,
  cycle: any,
  pond: any
}> = ({ isOpen, onClose, cycle, pond }) => {
  const isActive = cycle && !cycle.finished_at;

  const onPressFarmingSummary = useCallback(() => {
    JalaAnalytic.log('Mobile app: farm summary');
    navigate('Home.Stack', 'Home.Monitoring');
    onClose()
  }, [onClose])

  const onPressCycleHistory = useCallback(() => {
    JalaAnalytic.log('Mobile app: cycle history list');
    navigate('Home.Stack', 'Home.Cycle.History');
    onClose()
  }, [onClose])

  const onPressPondProfile = useCallback(() => {
    JalaAnalytic.log('Mobile app: pond setting');
    navigate('Home.Stack', 'Home.Farm.Pond.Setting', { ...pond });
    onClose();
  }, [onClose, pond])

  const onPressCycleProfile = useCallback(() => {
    JalaAnalytic.log('Mobile app: cycle setting');
    navigate('Home.Stack', 'Home.Cycle.EditMultiple', { models: [cycle] });
    onClose();
  }, [cycle, onClose])

  return (
    <Modal
      isVisible={isOpen}
      style={MODAL}
      onBackdropPress={onClose}
      backdropTransitionOutTiming={0}
      backdropColor={color.primary}
      animationIn="slideInLeft"
      animationOut="slideOutRight"
      animationInTiming={300}
      animationOutTiming={300}
      backdropTransitionInTiming={600}
    >
    {/* <Modal style={MODAL} isOpen={isOpen} onClose={onClose} animationPreset="slide"> */}
      <SafeAreaView />
      <View style={MODAL_CONTENT}>
        <ButtonMenu label="farmingSummary" icon="pc-hp" onPress={onPressFarmingSummary} />
        <ButtonMenu label="cycleHistory" icon="pond-step" onPress={onPressCycleHistory} />
        <ButtonMenu label="pondProfile" icon="pond" onPress={onPressPondProfile} />
        {isActive && <ButtonMenu label="cycleProfile" icon="cycle-profile" onPress={onPressCycleProfile} />}
      </View>
    </Modal>
  )
}

export const CycleDetailMore: FC = observer(function CycleDetailMore(props) {
  const { appStore: { farmStore: { pondStore: { pond, cycleStore: { cycle } } } } } = useStores();
  const { isOpen, onOpen, onClose } = useDisclose()

  return (
    <>
      <View style={TOP_BAR_CONTAINER}>
        <TouchableOpacity style={TOP_BAR_MORE_BUTTON} onPress={onOpen}>
          <JalaIcon name="menu" size={25} color={color.palette.white}/>
        </TouchableOpacity>
      </View>
      <MenuOptions cycle={cycle} pond={pond} onClose={onClose} isOpen={isOpen} />
    </>
  )
})
